import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Text, Button, Table, NumberInput, Group, Badge, Select, Checkbox, Modal, Combobox, InputBase, useCombobox, Paper, TextInput, Loader, FileInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconDownload, IconChevronDown, IconChevronRight, IconMail, IconTrash, IconSearch, IconRefresh, IconRepeat } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import api from '../services/api';
import { useTranslation } from '../context/TranslationContext';
import { formatPrice } from '../utils/formatPrice';
import { localizedField } from '../utils/localizedField';
import { weightToPieces, piecesToWeight, weightFromPieces } from '../utils/weightPieces';
import { usePrimaryTouched } from '../hooks/usePrimaryTouched';
import { isRunningOrder, isOpenRunningOrder, isClosedRunningOrder } from '../utils/runningOrder';
import FreeItemBadge from './FreeItemBadge';
import OngoingOrderModal from './OngoingOrderModal';

function getDisplayUnit(weightUnit) {
  return weightUnit || 'Stk';
}

function todayInputValue() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function orderedWeightDisplay(ordered, weightValue, weightUnit) {
  if (weightUnit && weightValue != null) {
    const total = Number(ordered) * Number(weightValue);
    return `${ordered} Stk = ${weightPrecision(total, weightUnit)} ${weightUnit}`;
  }
  return String(ordered);
}

function weightPrecision(value, weightUnit) {
  if (weightUnit === 'kg' || weightUnit === 'L') {
    return Number(value).toFixed(3);
  }
  return String(Math.round(Number(value)));
}

function inputPrecision(weightUnit) {
  if (weightUnit === 'kg' || weightUnit === 'L') return 3;
  return 0;
}

function inputStep(weightUnit) {
  if (weightUnit === 'kg' || weightUnit === 'L') return 0.001;
  return 1;
}

function fullyShipped(item) {
  // Adhoc lines (variant_id null) are never fulfilled — count them as shipped.
  if (!item.variant_id) return true;
  const shipped = Number(item.total_shipped || 0);
  if (item.weight_unit && item.weight_value != null) {
    const orderedWeight = Number(item.quantity) * Number(item.weight_value);
    return shipped >= orderedWeight;
  }
  return shipped >= Number(item.quantity);
}

function LotNumberField({ lotNumber, onSelect, availableLots = [], disabled, size = 'sm' }) {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });
  const [search, setSearch] = useState(lotNumber || '');

  useEffect(() => {
    setSearch(lotNumber || '');
  }, [lotNumber]);

  const options = (availableLots || []).map((lot) => ({
    value: lot.batch_id,
    label: lot.remaining != null
      ? `${lot.lot_number} (${lot.remaining} pcs left)${lot.reserved ? ' · Reserved' : ''}`
      : lot.lot_number,
    lot
  }));

  const filtered = options.filter((o) =>
    o.lot.lot_number.toLowerCase().includes(search.toLowerCase()) ||
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={(val) => {
        const opt = options.find((o) => o.value === val);
        if (opt) {
          onSelect(opt.lot);
          setSearch(opt.lot.lot_number);
        }
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <InputBase
          aria-label="Lot number"
          value={search}
          size={size}
          disabled={disabled}
          onChange={(event) => {
            setSearch(event.currentTarget.value);
            onSelect({ lot_number: event.currentTarget.value, batch_id: null });
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
          }}
          onBlur={() => combobox.closeDropdown()}
          onClick={() => combobox.openDropdown()}
          rightSection={<Combobox.Chevron />}
          rightSectionPointerEvents="none"
          placeholder="Lot No."
        />
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Options>
          {filtered.length === 0 ? (
            <Combobox.Empty>No matching lots</Combobox.Empty>
          ) : (
            filtered.map((o) => (
              <Combobox.Option value={o.value} key={o.value}>
                {o.label}
              </Combobox.Option>
            ))
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}

// Actions for an issued fulfillment awaiting its delivery note. Holds the
// note date printed on the PDF ("Datum:"), defaulting to today; both preview
// and send use it.
function IssuedFulfillmentActions({ onPreview, onSend }) {
  const [noteDate, setNoteDate] = useState(todayInputValue);
  return (
    <Group spacing="xs" align="flex-end">
      <DateInput
        size="xs"
        w={140}
        label="Note date"
        valueFormat="YYYY-MM-DD"
        value={noteDate}
        onChange={setNoteDate}
        clearable={false}
        aria-label="Delivery note date"
      />
      <Button size="xs" variant="light" onClick={() => onPreview(noteDate)}>
        Preview Delivery Note
      </Button>
      <Button size="xs" color="blue" leftSection={<IconMail size={12} />} onClick={() => onSend(noteDate)}>
        Send Note
      </Button>
    </Group>
  );
}

// Searchable picker that lets the admin replace an offer's free item with a
// different catalog item during fulfillment (e.g. no mango lassi → herb paneer).
function SubstitutePicker({ value, catalog = [], onChange, disabled }) {
  const data = (catalog || []).map(v => ({ value: v.id, label: `${v.name || v.sku} (${v.sku})` }));

  return (
    <Select
      data={data}
      value={value || null}
      onChange={onChange}
      disabled={disabled}
      searchable
      clearable
      placeholder="Select replacement"
      aria-label="Replacement item"
      size="xs"
      limit={20}
    />
  );
}

function FulfillOrderForm({ order, onFulfillSuccess }) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const { markTouched, isTouched } = usePrimaryTouched();

  const allocKeyRef = useRef(0);
  function newAllocKey() {
    allocKeyRef.current += 1;
    return `alloc-${allocKeyRef.current}`;
  }

  const faddKeyRef = useRef(0);
  function newFaddKey() {
    faddKeyRef.current += 1;
    return `fadd-${faddKeyRef.current}`;
  }

  // Compute default item data from order items.
  // Only product-linked lines are fulfillable — adhoc (variant_id null) lines are skipped.
  function buildItemsData(draftItems) {
    return order.items.filter(item => item.variant_id).map(item => {
      const shipped = Number(item.total_shipped || 0);
      const draftMatches = draftItems ? draftItems.filter(di => di.order_item_id === item.id) : [];
      const weightVal = Number(item.weight_value || 0);
      const weightUnit = item.weight_unit;
      const isWeightBased = !!(weightUnit && weightVal > 0);
      const allocations = draftMatches.length > 0
        ? draftMatches.map(di => ({
            key: newAllocKey(),
            quantity_shipped: Number(di.quantity_shipped) || 0,
            pcs: di.pcs_override != null
              ? Math.round(Number(di.pcs_override))
              : (isWeightBased ? calcPcsFromShipped(di.quantity_shipped, weightVal, weightUnit) : 0),
            lot_number: di.lot_number || '',
            batch_id: di.batch_id || null
          }))
        : [{ key: newAllocKey(), quantity_shipped: 0, pcs: 0, lot_number: '', batch_id: null }];
      return {
        order_item_id: item.id,
        product_sku: item.product_sku,
        name: localizedField(item, 'name', t.langCode) || item.name || item.product_sku,
        weight_value: item.weight_value,
        weight_unit: item.weight_unit,
        ordered: item.quantity,
        ordered_price: item.unit_price_final,
        is_free: Number(item.unit_price_final || 0) === 0,
        is_credit: Number(item.quantity || 0) < 0,
        substitute_variant_id: null,
        previously_shipped: shipped,
        available_lots: item.available_lots || [],
        allocations
      };
    });
  }

  // Normalize persisted free add-on rows (fulfillment_items with order_item_id
  // NULL, carrying pcs_override/quantity_shipped) back into the editor state
  // shape {key, variant_id, pcs, lot_number, batch_id}.
  function buildFreeAdds(draftItems) {
    return (draftItems || [])
      .filter(i => !i.order_item_id)
      .map(i => ({
        key: newFaddKey(),
        variant_id: i.variant_id,
        pcs: i.pcs_override != null
          ? Math.round(Number(i.pcs_override))
          : Math.round(Number(i.quantity_shipped)),
        lot_number: i.lot_number || '',
        batch_id: i.batch_id || null
      }));
  }

  const drafts = (order.fulfillments || []).filter(f => f.status === 'draft');
  // Which draft the form is currently editing (null = a new shipment). Drafts
  // are never discarded; multiple may exist at once, so the admin picks one to
  // work on and a reopened delivery note becomes a separate selectable draft
  // instead of overwriting the one being edited.
  const [activeDraftId, setActiveDraftId] = useState(() => drafts[0]?.id ?? null);
  const activeDraft = drafts.find(d => d.id === activeDraftId) || null;
  const [itemsData, setItemsData] = useState(() => buildItemsData(activeDraft?.items || null));
  const [note, setNote] = useState(activeDraft?.note || '');
  const [substitutes, setSubstitutes] = useState([]);
  const [reopenId, setReopenId] = useState(null);
  // Free add-ons: bonus items given during fulfillment, not part of the order's
  // line items. Stored as fulfillment rows with order_item_id NULL.
  const [freeAdds, setFreeAdds] = useState(() => buildFreeAdds(activeDraft?.items));
  // Which issued fulfillments have their item table expanded (all collapsed by default).
  const [expandedFulfillments, setExpandedFulfillments] = useState({});

  const toggleFulfillment = (id) => {
    setExpandedFulfillments(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleFulfillmentKeyDown = (e, id) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleFulfillment(id);
    }
  };

  // Re-initialize when the order changes (after save/fulfill/reopen/refresh).
  // null = "New shipment" and is always a valid, stable selection. Only snap
  // away from a draft selection that no longer exists (e.g. it was just
  // shipped) — reset to the new-shipment view rather than forcing another
  // draft. A reopened delivery note becomes a new draft and is never auto-
  // selected over the one being edited.
  useEffect(() => {
    const draftsNow = (order.fulfillments || []).filter(f => f.status === 'draft');
    const selectedId = (activeDraftId === null || draftsNow.some(d => d.id === activeDraftId)) ? activeDraftId : null;
    if (selectedId !== activeDraftId) setActiveDraftId(selectedId);
    const selected = draftsNow.find(d => d.id === selectedId) || null;
    setItemsData(buildItemsData(selected?.items || null));
    // Drafts carry free add-on rows (order_item_id NULL) back into the editor;
    // issued fulfillments' free adds stay read-only in "Past Fulfillments".
    setFreeAdds(buildFreeAdds(selected?.items));
    setNote(selected?.note || '');
  }, [order.id, order.fulfillments, activeDraftId]);

  // Load the lightweight catalog for the free-item replacement picker.
  useEffect(() => {
    let cancelled = false;
    api.getFulfillmentSubstitutes(t.langCode)
      .then(data => { if (!cancelled) setSubstitutes(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setSubstitutes([]); });
    return () => { cancelled = true; };
  }, [t.langCode]);

  const isFullyShipped = order.items.every(fullyShipped);
  const isReconciled = order.is_reconciled;
  const canFulfill = !isReconciled && !isFullyShipped;
  const showFulfillForm = canFulfill || drafts.length > 0;

  const issuedFulfillments = (order.fulfillments || []).filter(f => f.status !== 'draft');

  function calcPcsFromShipped(qtyShipped, weightValue, weightUnit) {
    if (!weightValue) return 0;
    const pieces = weightToPieces(qtyShipped, weightUnit, weightValue, weightUnit, { roundMode: 'round' });
    return pieces === null ? 0 : pieces;
  }

  const handleUpdateAllocation = (itemId, allocKey, field, value) => {
    const current = itemsData.find(it => it.order_item_id === itemId);
    const weightVal = Number(current?.weight_value || 0);
    const isWeightBased = !!(current?.weight_unit && weightVal > 0);
    // The weight (quantity_shipped) is the primary field: once the user edits
    // it, pcs overrides stop back-propagating into it (like quick order).
    if (field === 'quantity_shipped' && isWeightBased) markTouched(allocKey);
    setItemsData(prev => prev.map(it => {
      if (it.order_item_id !== itemId) return it;
      return {
        ...it,
        allocations: it.allocations.map(a => {
          if (a.key !== allocKey) return a;
          const updated = { ...a, [field]: value };
          if (field === 'quantity_shipped' && it.weight_value) {
            updated.pcs = calcPcsFromShipped(value, it.weight_value, it.weight_unit);
          } else if (field === 'pcs' && isWeightBased && !isTouched(allocKey)) {
            const weight = weightFromPieces(value, it.weight_value, it.weight_unit, it.weight_unit);
            if (weight !== null) updated.quantity_shipped = weight;
          }
          return updated;
        })
      };
    }));
  };

  const handleSelectLot = (itemId, allocKey, lot) => {
    setItemsData(prev => prev.map(it => {
      if (it.order_item_id !== itemId) return it;
      return {
        ...it,
        allocations: it.allocations.map(a =>
          a.key === allocKey
            ? { ...a, lot_number: (lot.lot_number || '').trim(), batch_id: lot.batch_id || null }
            : a
        )
      };
    }));
  };

  const handleAddAllocation = (itemId) => {
    setItemsData(prev => prev.map(it => {
      if (it.order_item_id !== itemId) return it;
      return {
        ...it,
        allocations: [...it.allocations, { key: newAllocKey(), quantity_shipped: 0, pcs: 0, lot_number: '', batch_id: null }]
      };
    }));
  };

  const handleRemoveAllocation = (itemId, allocKey) => {
    setItemsData(prev => prev.map(it => {
      if (it.order_item_id !== itemId || it.allocations.length <= 1) return it;
      return { ...it, allocations: it.allocations.filter(a => a.key !== allocKey) };
    }));
  };

  // --- Free add-ons ----------------------------------------------------------

  const handleAddFreeItem = () => {
    setFreeAdds(prev => [
      ...prev,
      { key: newFaddKey(), variant_id: null, pcs: 0, lot_number: '', batch_id: null }
    ]);
  };

  const handleUpdateFreeItem = (key, field, value) => {
    setFreeAdds(prev => prev.map(fa =>
      fa.key === key ? { ...fa, [field]: value } : fa
    ));
  };

  const handleSelectFreeLot = (key, lot) => {
    setFreeAdds(prev => prev.map(fa =>
      fa.key === key ? { ...fa, lot_number: (lot.lot_number || '').trim(), batch_id: lot.batch_id || null } : fa
    ));
  };

  const handleRemoveFreeItem = (key) => {
    setFreeAdds(prev => prev.filter(fa => fa.key !== key));
  };

  function getFulfillmentItems() {
    const items = itemsData.flatMap(it => {
      const weightVal = Number(it.weight_value || 0);
      const isWeightBased = !!(it.weight_unit && weightVal > 0);
      return it.allocations
        .map(alloc => {
          const effectiveQty = Number(alloc.quantity_shipped) > 0
            ? Number(alloc.quantity_shipped)
            : (isWeightBased && Number(alloc.pcs) > 0 ? piecesToWeight(alloc.pcs, it.weight_value, it.weight_unit) : 0);
          return {
            order_item_id: it.order_item_id,
            product_sku: it.product_sku,
            quantity_shipped: effectiveQty,
            lot_number: (alloc.lot_number || '').trim(),
            batch_id: alloc.batch_id || null,
            pcs: alloc.pcs || null,
            substitute_variant_id: it.is_free ? (it.substitute_variant_id || null) : null
          };
        })
        .filter(row => Number(row.quantity_shipped) > 0);
    });
    // Free add-ons are entered in pieces. For weight-based variants the
    // delivered quantity is stored as a weight (like regular lines, so the
    // delivery note/lot math is consistent); piece-based variants store pieces.
    const freeRows = freeAdds
      .filter(fa => fa.variant_id)
      .map(fa => {
        const sub = substitutes.find(s => s.id === fa.variant_id) || null;
        const faWeightBased = !!(sub?.weight_unit && Number(sub?.weight_value) > 0);
        const pcs = Number(fa.pcs) || 0;
        const effectiveQty = faWeightBased && pcs > 0
          ? piecesToWeight(pcs, sub.weight_value, sub.weight_unit)
          : pcs;
        return {
          variant_id: fa.variant_id,
          quantity_shipped: effectiveQty,
          pcs: pcs || null,
          lot_number: (fa.lot_number || '').trim(),
          batch_id: fa.batch_id || null
        };
      })
      .filter(row => Number(row.pcs) > 0);
    return [...items, ...freeRows];
  }

  function validateLotNumbers(fulfillmentItems) {
    const missingLot = fulfillmentItems.find(it => !it.lot_number);
    if (missingLot) {
      notifications.show({
        title: 'Lot number required',
        message: `Please enter a lot number for ${missingLot.product_sku}.`,
        color: 'red'
      });
      return false;
    }
    return true;
  }

  const handleFulfill = async () => {
    const fulfillmentItems = getFulfillmentItems();
    if (fulfillmentItems.length === 0) {
      notifications.show({ title: 'No items', message: 'You must ship at least some quantity.', color: 'red' });
      return;
    }
    if (!validateLotNumbers(fulfillmentItems)) return;

    setSubmitting(true);
    const notifId = notifications.show({ loading: true, title: 'Shipping', message: 'Recording shipment...', autoClose: false });

    try {
      const payload = { fulfillmentItems, note };
      if (activeDraft) payload.fulfillmentId = activeDraft.id;
      await api.fulfillOrder(order.id, payload);
      notifications.update({ id: notifId, color: 'teal', title: 'Success', message: 'Order marked as shipped! Generate & send the delivery note separately.', autoClose: 5000 });
      onFulfillSuccess();
    } catch (err) {
      notifications.update({ id: notifId, color: 'red', title: 'Error', message: err.message, autoClose: 5000 });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    const fulfillmentItems = getFulfillmentItems();
    if (fulfillmentItems.length === 0) {
      notifications.show({ title: 'No items', message: 'You must ship at least some quantity.', color: 'red' });
      return;
    }

    setSubmitting(true);
    const notifId = notifications.show({ loading: true, title: 'Saving Draft', message: 'Saving draft...', autoClose: false });

    try {
      const draftPayload = { fulfillmentItems, note };
      if (activeDraft) {
        await api.updateFulfillDraft(activeDraft.id, draftPayload);
      } else {
        // No draft selected → create a fresh draft so multiple drafts stay open.
        const created = await api.saveFulfillDraft(order.id, draftPayload);
        if (created.fulfillmentId) setActiveDraftId(created.fulfillmentId);
      }
      notifications.update({ id: notifId, color: 'teal', title: 'Draft Saved', message: 'Draft fulfillment saved!', autoClose: 3000 });
      onFulfillSuccess();
    } catch (err) {
      notifications.update({ id: notifId, color: 'red', title: 'Error', message: err.message, autoClose: 5000 });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSelectSubstitute = (itemId, variantId) => {
    const original = order.items.find(i => i.id === itemId);
    const origVal = Number(original?.weight_value || 0);
    const origUnit = original?.weight_unit || null;
    const origWeightBased = !!(origUnit && origVal > 0);

    setItemsData(prev => prev.map(it => {
      if (it.order_item_id !== itemId) return it;
      const sub = substitutes.find(s => s.id === variantId) || null;

      // Units currently in effect on the row (the previously effective product).
      const curVal = Number(it.weight_value) || 0;
      const curUnit = it.weight_unit || null;
      const curWeightBased = !!(curUnit && curVal > 0);

      // Target units: the substitute's when one is chosen, the original item's
      // when the replacement is cleared.
      const subVal = sub ? (Number(sub.weight_value) || 0) : origVal;
      const subUnit = sub ? (sub.weight_unit || null) : origUnit;
      const targetWeightBased = sub ? !!(subUnit && subVal > 0) : origWeightBased;

      // Re-express every allocation in the target units. The piece count is
      // unit-independent (stock/orders are piece-based), so swapping e.g. a
      // 200g ghee jar for a 250g paneer block keeps 1 piece but re-derives
      // the shipped weight in the substitute's units.
      const allocations = it.allocations.map(a => {
        let pcs = Number(a.pcs || 0);
        if (!(pcs > 0) && Number(a.quantity_shipped) > 0) {
          pcs = curWeightBased
            ? calcPcsFromShipped(a.quantity_shipped, curVal, curUnit)
            : Math.round(Number(a.quantity_shipped));
        }
        const count = pcs > 0 ? Math.round(pcs) : 0;
        if (targetWeightBased) {
          const weight = count > 0 ? (weightFromPieces(count, subVal, subUnit, subUnit) ?? 0) : 0;
          return { ...a, pcs: count, quantity_shipped: weight };
        }
        return { ...a, pcs: count, quantity_shipped: count };
      });

      return {
        ...it,
        substitute_variant_id: variantId,
        weight_value: targetWeightBased ? subVal : 0,
        weight_unit: targetWeightBased ? subUnit : null,
        available_lots: sub?.available_lots || original?.available_lots || [],
        allocations
      };
    }));
  };

  const handleReopen = async () => {
    if (!reopenId) return;
    setSubmitting(true);
    const notifId = notifications.show({ loading: true, title: 'Reopening', message: 'Reopening fulfillment for editing...', autoClose: false });
    try {
      await api.reopenFulfillment(reopenId);
      notifications.update({ id: notifId, color: 'teal', title: 'Reopened', message: 'Fulfillment reopened — edit items below and re-ship.', autoClose: 4000 });
      setReopenId(null);
      onFulfillSuccess();
    } catch (err) {
      notifications.update({ id: notifId, color: 'red', title: 'Error', message: err.message, autoClose: 5000 });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendDeliveryNote = async (fulfillmentId) => {
    const notifId = notifications.show({ loading: true, title: 'Sending', message: 'Sending delivery note email...', autoClose: false });
    try {
      await api.sendDeliveryNote(fulfillmentId);
      notifications.update({ id: notifId, color: 'teal', title: 'Success', message: 'Delivery note resent to customer!', autoClose: 3000 });
    } catch (err) {
      notifications.update({ id: notifId, color: 'red', title: 'Error', message: err.message, autoClose: 5000 });
    }
  };

  const handleIssueDeliveryNote = async (fulfillmentId, date) => {
    const notifId = notifications.show({ loading: true, title: 'Sending', message: 'Generating and sending delivery note...', autoClose: false });
    try {
      await api.sendDeliveryNote(fulfillmentId, date);
      notifications.update({ id: notifId, color: 'teal', title: 'Success', message: 'Delivery note generated and sent to customer!', autoClose: 3000 });
      onFulfillSuccess();
    } catch (err) {
      notifications.update({ id: notifId, color: 'red', title: 'Error', message: err.message, autoClose: 5000 });
    }
  };

  const openPdfBlob = (blob) => {
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const handlePreview = async () => {
    const fulfillmentItems = getFulfillmentItems();
    if (fulfillmentItems.length === 0) {
      notifications.show({ title: 'No items', message: 'Add quantities before previewing.', color: 'red' });
      return;
    }
    try {
      const blob = await api.previewFulfillment(order.id, fulfillmentItems, note);
      openPdfBlob(blob);
    } catch (err) {
      notifications.show({ title: 'Preview Error', message: err.message, color: 'red', autoClose: 5000 });
    }
  };

  const handlePreviewIssuedNote = async (fulfillmentId, date) => {
    try {
      const blob = await api.previewDeliveryNote(fulfillmentId, date);
      openPdfBlob(blob);
    } catch (err) {
      notifications.show({ title: 'Preview Error', message: err.message, color: 'red', autoClose: 5000 });
    }
  };

  const renderAllocCells = (item, alloc, showRemove) => {
    const unit = getDisplayUnit(item.weight_unit);
    const isWeightBased = !!(item.weight_unit && item.weight_value);
    const isCredit = item.is_credit;
    return (
      <>
        <Table.Td style={{ width: 110 }}>
          <NumberInput
            value={alloc.quantity_shipped}
            onChange={(v) => handleUpdateAllocation(item.order_item_id, alloc.key, 'quantity_shipped', v)}
            min={0}
            step={inputStep(item.weight_unit)}
            precision={inputPrecision(item.weight_unit)}
            hideControls
            placeholder={unit}
            rightSection={<Text size="xs" style={{ pointerEvents: 'none', color: '#888' }}>{unit}</Text>}
            rightSectionWidth={40}
            style={{ maxWidth: 110 }}
            disabled={isCredit}
            readOnly={isCredit}
          />
        </Table.Td>
        <Table.Td style={{ width: 90 }}>
          <NumberInput
            value={isWeightBased ? alloc.pcs : alloc.quantity_shipped}
            onChange={(v) => {
              if (isWeightBased) {
                handleUpdateAllocation(item.order_item_id, alloc.key, 'pcs', v);
              } else {
                handleUpdateAllocation(item.order_item_id, alloc.key, 'quantity_shipped', v);
              }
            }}
            min={0}
            step={1}
            precision={0}
            hideControls
            placeholder="Stk"
            rightSection={<Text size="xs" style={{ pointerEvents: 'none', color: '#888' }}>Stk</Text>}
            rightSectionWidth={35}
            style={{ maxWidth: 90 }}
            disabled={isCredit}
            readOnly={isCredit}
          />
        </Table.Td>
        <Table.Td style={{ width: 160 }}>
          <LotNumberField
            lotNumber={alloc.lot_number}
            onSelect={(lot) => handleSelectLot(item.order_item_id, alloc.key, lot)}
            availableLots={item.available_lots}
            disabled={isCredit}
          />
        </Table.Td>
        <Table.Td style={{ width: 70, textAlign: 'center', verticalAlign: 'middle' }}>
          {showRemove && (
            <Button size="compact-xs" variant="subtle" color="red" aria-label="Remove lot" onClick={() => handleRemoveAllocation(item.order_item_id, alloc.key)}>
              <IconTrash size={14} />
            </Button>
          )}
        </Table.Td>
      </>
    );
  };

  return (
    <Paper withBorder p="md">

      {/* Past Fulfillments (issued + drafts info) */}
      {order.fulfillments && order.fulfillments.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h5>Past Fulfillments</h5>
          {issuedFulfillments.map((f) => {
            const fItems = f.items || [];
            const isDelivered = !!f.delivery_note_file_id;
            const isExpanded = !!expandedFulfillments[f.id];
            const detailId = `fulfillment-detail-${f.id}`;
            return (
              <div key={f.id} style={{ border: '1px solid #eee', borderRadius: 4, marginBottom: 8, padding: '0.5rem' }}>
                <Group
                  justify="apart"
                  mb={isExpanded ? 'xs' : 0}
                  onClick={() => toggleFulfillment(f.id)}
                  onKeyDown={(e) => handleFulfillmentKeyDown(e, f.id)}
                  tabIndex={0}
                  role="button"
                  aria-expanded={isExpanded}
                  aria-controls={detailId}
                  aria-label={`Toggle details for delivery note ${f.id.substring(0, 8)}`}
                  style={{ cursor: 'pointer' }}
                >
                  <div>
                    <Group gap={6}>
                      {isExpanded
                        ? <IconChevronDown size={14} aria-hidden="true" />
                        : <IconChevronRight size={14} aria-hidden="true" />}
                      <Text size="sm"><b>Delivery Note #{f.id.substring(0,8)}</b></Text>
                    </Group>
                    <Text size="xs" c="dimmed">
                      Shipped: {f.fulfilled_at ? new Date(f.fulfilled_at).toLocaleDateString() : new Date(f.created_at).toLocaleDateString()}
                      {isDelivered
                        ? ` · Delivered: ${f.delivered_at ? new Date(f.delivered_at).toLocaleDateString() : new Date(f.created_at).toLocaleDateString()}`
                        : ' · Awaiting delivery note'}
                    </Text>
                    {f.note && (
                      <Text size="xs" c="dimmed" mt={2}>Note: {f.note}</Text>
                    )}
                  </div>
                  <Group spacing="xs" align="flex-end" onClick={(e) => e.stopPropagation()}>
                    {isDelivered ? (
                      <>
                        <Button size="xs" variant="light" onClick={() => window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/orders/fulfillments/${f.id}/delivery-note`, '_blank')}>
                          Download
                        </Button>
                        <Button size="xs" variant="light" color="green" leftSection={<IconMail size={12} />} onClick={() => handleSendDeliveryNote(f.id)}>
                          Resend
                        </Button>
                      </>
                    ) : (
                      <IssuedFulfillmentActions
                        onPreview={(date) => handlePreviewIssuedNote(f.id, date)}
                        onSend={(date) => handleIssueDeliveryNote(f.id, date)}
                      />
                    )}
                    <Button size="xs" variant="subtle" color="orange" leftSection={<IconRefresh size={12} />} onClick={() => setReopenId(f.id)}>
                      Reopen
                    </Button>
                  </Group>
                </Group>
                {isExpanded && fItems.length > 0 && (
                  <Table size="sm" withRowBorders id={detailId}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th scope="col">Product</Table.Th>
                        <Table.Th scope="col" ta="right">Qty Shipped</Table.Th>
                        <Table.Th scope="col">Lot No.</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {fItems.map(fi => {
                        const oi = order.items.find(i => i.id === fi.order_item_id);
                        const freeAdd = !oi && fi.variant_id ? (substitutes.find(s => s.id === fi.variant_id) || null) : null;
                        const name = oi
                          ? (localizedField(oi, 'name', t.langCode) || oi.name || oi.product_sku || fi.product_sku)
                          : (freeAdd?.name || fi.product_sku);
                        return (
                          <Table.Tr key={fi.id || fi.order_item_id}>
                            <Table.Td>
                              {name}
                              {freeAdd && (
                                <Text size="xs" c="green.7" mt={1}>Free add-on</Text>
                              )}
                              {oi?.substitution_note && (
                                <Text size="xs" c="teal.7" mt={1}>
                                  {t('substitutionNote', '', { product: localizedField(oi, 'name', t.langCode) || oi.name || oi.product_sku || '' })}
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td ta="right" style={{ whiteSpace: 'nowrap' }}>
                              {fi.quantity_shipped}
                              {oi?.weight_unit ? ` ${oi.weight_unit}` : (freeAdd?.weight_unit ? ` ${freeAdd.weight_unit}` : '')}
                            </Table.Td>
                            <Table.Td>{fi.lot_number || ''}</Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Fulfillment Form */}
      {showFulfillForm && (
        <div style={{ border: '1px solid #eee', borderRadius: 4, marginBottom: '1.5rem', padding: '0.5rem 1rem 1rem' }}>
          <h5>Record Shipment</h5>
          {drafts.length > 0 && (
            <Select
              size="xs"
              label="Working on"
              mb="sm"
              w={360}
              allowDeselect={false}
              aria-label="Select which draft to edit"
              data={[
                { value: '__new__', label: 'New shipment' },
                ...drafts.map(d => ({ value: d.id, label: `Draft #${d.id.substring(0, 8)}` }))
              ]}
              value={activeDraftId || '__new__'}
              onChange={(v) => setActiveDraftId(v === '__new__' ? null : v)}
            />
          )}
          {activeDraft && (
            <Text size="sm" mb="sm" c="orange.7">
              <b>Editing draft #{activeDraft.id.substring(0,8)}</b> — edits below overwrite this draft only. Choosing &quot;New shipment&quot; starts another draft alongside it.
            </Text>
          )}
          <Table mb="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: '24%' }}>Product</Table.Th>
                <Table.Th style={{ whiteSpace: 'nowrap', width: 90 }}>Ordered</Table.Th>
                <Table.Th style={{ whiteSpace: 'nowrap', width: 90 }}>Shipped Total</Table.Th>
                <Table.Th style={{ width: 110 }}>Weight</Table.Th>
                <Table.Th style={{ width: 90 }}>Pcs</Table.Th>
                <Table.Th style={{ width: 160 }}>Lot Number</Table.Th>
                <Table.Th style={{ width: 70 }}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {itemsData.map(item => {
                const rawItem = order.items.find(i => i.id === item.order_item_id);
                const isFree = Number(item.ordered_price || 0) === 0 && !(item.is_credit);
                const isCredit = item.is_credit;
                const isFullyShippedItem = isCredit ? false : fullyShipped(rawItem);
                let rowBg = undefined;
                if (isFullyShippedItem) rowBg = '#dcfce7';
                else if (isFree) rowBg = '#f0fdf4';
                else if (isCredit) rowBg = '#f1f5f9'; // greyed out for credit/refund lines
                const rowStyle = rowBg ? { background: rowBg } : {};
                const showRemove = item.allocations.length > 1;
                const rowSpan = item.allocations.length + 1;
                return (
                  <React.Fragment key={item.order_item_id}>
                    <Table.Tr style={rowStyle}>
                      <Table.Td rowSpan={rowSpan} style={{ wordBreak: 'break-word', verticalAlign: 'middle' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span>{item.name}</span>
                          {isFree && <FreeItemBadge />}
                        </span>
                        {isFree && (
                          <div style={{ marginTop: 6, maxWidth: 240 }}>
                            <Text size="xs" c="dimmed" mb={2}>Replacement (optional)</Text>
                            <SubstitutePicker
                              value={item.substitute_variant_id}
                              catalog={substitutes}
                              disabled={submitting}
                              onChange={(v) => handleSelectSubstitute(item.order_item_id, v)}
                            />
                          </div>
                        )}
                        {rawItem?.substitution_note && (
                          <Text size="xs" c="teal.7" mt={2}>{t('substitutionNote', '', { product: localizedField(rawItem, 'name', t.langCode) || rawItem.name || rawItem.product_sku || '' })}</Text>
                        )}
                      </Table.Td>
                      <Table.Td rowSpan={rowSpan} style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{orderedWeightDisplay(item.ordered, item.weight_value, item.weight_unit)}</Table.Td>
                      <Table.Td rowSpan={rowSpan} style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{weightPrecision(item.previously_shipped, item.weight_unit)}</Table.Td>
                      {renderAllocCells(item, item.allocations[0], showRemove)}
                    </Table.Tr>
                    {item.allocations.slice(1).map(alloc => (
                      <Table.Tr key={alloc.key} style={rowStyle}>
                        {renderAllocCells(item, alloc, showRemove)}
                      </Table.Tr>
                    ))}
                    <Table.Tr style={rowStyle}>
                      <Table.Td colSpan={4} style={{ padding: '2px 6px', textAlign: 'right' }}>
                        <Button size="compact-xs" variant="light" onClick={() => handleAddAllocation(item.order_item_id)}>
                          + Add Lot
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  </React.Fragment>
                );
              })}
            </Table.Tbody>
          </Table>

          {/* Free add-ons: bonus items given during fulfillment, not on the order */}
          <Paper withBorder p="sm" mb="md" radius="sm" bg="yellow.0">
            <Group justify="apart" mb={freeAdds.length ? 'xs' : 0}>
              <Text size="sm" fw={600}>Free Add-ons (bonus items)</Text>
              <Button size="compact-xs" variant="light" color="yellow" onClick={handleAddFreeItem} disabled={submitting}>
                + Add Free Item
              </Button>
            </Group>
            {freeAdds.length === 0 && (
              <Text size="xs" c="dimmed">Optionally give the customer a free extra item with this delivery. It appears on the delivery note but not on the invoice, and consumes stock.</Text>
            )}
            {freeAdds.map(fa => {
              const sub = substitutes.find(s => s.id === fa.variant_id) || null;
              const weightBased = !!(sub?.weight_unit && Number(sub?.weight_value) > 0);
              return (
                <Group key={fa.key} gap="sm" align="flex-end" mt="xs">
                  <Select
                    size="xs"
                    data={(substitutes || []).map(v => ({ value: v.id, label: `${v.name || v.sku} (${v.sku})` }))}
                    value={fa.variant_id || null}
                    onChange={(v) => handleUpdateFreeItem(fa.key, 'variant_id', v)}
                    clearable
                    searchable
                    limit={20}
                    placeholder="Select free item"
                    aria-label="Free add-on item"
                    w={260}
                  />
                  <NumberInput
                    size="xs"
                    value={fa.pcs}
                    onChange={(v) => handleUpdateFreeItem(fa.key, 'pcs', v)}
                    min={0}
                    step={1}
                    precision={0}
                    hideControls
                    placeholder="Pcs"
                    aria-label="Free add-on quantity in pieces"
                    rightSection={<Text size="xs" style={{ pointerEvents: 'none', color: '#888' }}>Stk</Text>}
                    rightSectionWidth={35}
                    w={90}
                  />
                  <div style={{ width: 168 }}>
                    <LotNumberField
                      lotNumber={fa.lot_number}
                      onSelect={(lot) => handleSelectFreeLot(fa.key, lot)}
                      availableLots={sub?.available_lots || []}
                      size="xs"
                    />
                  </div>
                  {weightBased && (
                    <Text size="xs" c="dimmed">{sub.weight_value}{sub.weight_unit} / pc</Text>
                  )}
                  <Button size="compact-xs" variant="subtle" color="red" aria-label="Remove free item" onClick={() => handleRemoveFreeItem(fa.key)}>
                    <IconTrash size={14} />
                  </Button>
                </Group>
              );
            })}
          </Paper>

          <TextInput
            label="Fulfillment note (optional)"
            placeholder="e.g. left with neighbour, replaced free item, etc."
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            mb="md"
            maxLength={500}
            aria-label="Fulfillment note"
          />
          <Group justify="flex-end">
            <Button onClick={handleSaveDraft} loading={submitting} variant="light" color="yellow">
              {activeDraft ? 'Save Draft' : 'Save as Draft'}
            </Button>
            <Button onClick={handlePreview} variant="light" color="gray">
              Preview Delivery Note
            </Button>
            <Button onClick={handleFulfill} loading={submitting} color="blue">
              Ship
            </Button>
          </Group>
          <Text size="xs" c="dimmed" mt="xs">Ship records the shipment with the shipping date. The delivery note is generated & sent later, at the delivery date.</Text>
        </div>
      )}

      {!showFulfillForm && (
        <Group>
          {isFullyShipped ? <Badge color="green" size="lg">All items fully shipped</Badge> : null}
          {isReconciled && (
            <Badge color="blue" size="lg">Costs Adjusted & Closed</Badge>
          )}
        </Group>
      )}

      <Modal opened={!!reopenId} onClose={() => setReopenId(null)} title="Reopen fulfillment?" centered>
        <Text mb="md">
          This reopens the fulfillment for editing so you can fix mistakes. It will be set back to draft and the
          delivery note must be regenerated before it can be sent again. Re-ship to record the corrected items.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setReopenId(null)}>Cancel</Button>
          <Button color="orange" onClick={handleReopen} loading={submitting}>Reopen & Edit</Button>
        </Group>
      </Modal>
    </Paper>
  );
}

function RefundOrderForm({ order, onRefundSuccess }) {
  const { t } = useTranslation();
  // Only product-linked lines are refundable — adhoc (variant_id null) lines are skipped.
  const [itemsData, setItemsData] = useState(() => {
    return order.items.filter(item => item.variant_id).map(item => {
      const refunded = Number(item.total_refunded || 0);
      const maxRefundable = Math.round(((Number(item.unit_price_final) * item.quantity) - refunded) * 100) / 100;
      return {
        order_item_id: item.id,
        product_sku: item.product_sku,
        name: localizedField(item, 'name', t.langCode) || item.name || item.product_sku,
        ordered: item.quantity,
        unit_price: Number(item.unit_price_final),
        max_refundable: maxRefundable,
        quantity_refunded: 0,
        refund_qty: 0,
        calculation_type: 'fixed',
        percentage_rate: 50,
        refund_amount: 0
      };
    });
  });

  const [refundMethod, setRefundMethod] = useState(order.payment_method === 'card' ? 'account_credit' : 'account_credit');
  const [isFullOrderCancellation, setIsFullOrderCancellation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [reason, setReason] = useState('');
  const [images, setImages] = useState([]);

  useEffect(() => {
    if (isFullOrderCancellation) {
      setItemsData(prev => prev.map(it => ({
        ...it,
        refund_amount: it.max_refundable,
        calculation_type: 'fixed',
        percentage_rate: 100
      })));
    } else {
      setItemsData(prev => prev.map(it => ({
        ...it,
        refund_amount: 0,
        calculation_type: 'fixed',
        percentage_rate: 50
      })));
    }
  }, [isFullOrderCancellation]);

  const buildRefundItems = () => {
    return itemsData
      .filter(it => {
        if (it.calculation_type === 'quantity') return Number(it.refund_qty) > 0;
        return Number(it.refund_amount) > 0;
      })
      .map(it => ({
        order_item_id: it.order_item_id,
        quantity_refunded: it.calculation_type === 'quantity'
          ? it.refund_qty
          : (it.quantity_refunded || it.ordered),
        calculation_type: it.calculation_type,
        percentage_rate: it.calculation_type === 'percentage' ? it.percentage_rate : undefined,
        refund_amount: it.refund_amount
      }));
  };

  const handleCalculationTypeChange = (id, type) => {
    setItemsData(prev => prev.map(it => {
      if (it.order_item_id !== id) return it;
      const updated = { ...it, calculation_type: type };
      if (type === 'percentage') {
        updated.refund_amount = it.max_refundable * (it.percentage_rate / 100);
      } else if (type === 'quantity') {
        updated.refund_amount = Math.round((it.unit_price * (it.refund_qty || 0)) * 100) / 100;
      } else {
        updated.refund_amount = 0;
      }
      return updated;
    }));
  };

  const handlePercentageChange = (id, rate) => {
    setItemsData(prev => prev.map(it => {
      if (it.order_item_id !== id) return it;
      return { ...it, percentage_rate: rate, refund_amount: it.max_refundable * (rate / 100) };
    }));
  };

  const handleQuantityChange = (id, qty) => {
    setItemsData(prev => prev.map(it => {
      if (it.order_item_id !== id) return it;
      const q = parseFloat(qty) || 0;
      return { ...it, refund_qty: q, refund_amount: Math.round((it.unit_price * q) * 100) / 100 };
    }));
  };

  const handleRefundAmountChange = (id, amount) => {
    setItemsData(prev => prev.map(it => it.order_item_id === id ? { ...it, refund_amount: parseFloat(amount) || 0 } : it));
  };

  const totalRefundAmount = itemsData.reduce((sum, it) => {
    if (it.calculation_type === 'quantity') return sum + (Number(it.refund_qty) > 0 ? Math.round((it.unit_price * (it.refund_qty || 0)) * 100) / 100 : 0);
    return sum + Number(it.refund_amount || 0);
  }, 0);

  const getRefundPayload = () => ({
    refund_method: refundMethod,
    is_full_order_cancellation: isFullOrderCancellation,
    reason,
    images: images.map(img => img.file_id),
    items: buildRefundItems()
  });

  const openPdfBlob = (blob) => {
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const handlePreview = async () => {
    const refundItems = buildRefundItems();
    if (refundItems.length === 0 || totalRefundAmount <= 0) {
      notifications.show({ title: 'No items', message: 'You must refund at least some amount.', color: 'red' });
      return;
    }
    setPreviewing(true);
    try {
      const blob = await api.previewRefund(order.id, getRefundPayload());
      openPdfBlob(blob);
    } catch (err) {
      notifications.show({ title: 'Preview Error', message: err.message, color: 'red', autoClose: 5000 });
    } finally {
      setPreviewing(false);
    }
  };

  const handleRefund = async () => {
    const refundItems = buildRefundItems();
    if (refundItems.length === 0 || totalRefundAmount <= 0) {
      notifications.show({ title: 'No items', message: 'You must refund at least some amount.', color: 'red' });
      return;
    }

    setSubmitting(true);
    const notifId = notifications.show({ loading: true, title: 'Processing', message: 'Creating refund...', autoClose: false });

    try {
      await api.createRefund(order.id, {
        refund_method: refundMethod,
        is_full_order_cancellation: isFullOrderCancellation,
        reason,
        images: images.map(img => img.file_id),
        items: refundItems
      });
      notifications.update({ id: notifId, color: 'teal', title: 'Success', message: 'Refund processed! Credit note sent.', autoClose: 3000 });
      onRefundSuccess();
    } catch (err) {
      notifications.update({ id: notifId, color: 'red', title: 'Error', message: err.message, autoClose: 5000 });
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;
    const fd = new FormData();
    Array.from(files).forEach((f, i) => fd.append('files', f, f.name || `refund-image-${i}`));
    try {
      const res = await api.uploadFiles(fd);
      const uploaded = (res.uploaded || []).map(f => ({
        file_id: f.id,
        name: f.filename,
        previewUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/files/${f.id}`
      }));
      setImages(prev => [...prev, ...uploaded]);
    } catch (err) {
      notifications.show({ title: 'Upload Error', message: err.message, color: 'red', autoClose: 5000 });
    }
  };

  const removeImage = (fileId) => {
    setImages(prev => prev.filter(img => img.file_id !== fileId));
  };

  return (
    <Paper withBorder p="md">

      <Group mb="md">
        <Checkbox
          label="Full Order Cancellation (includes shipping/VAT)"
          checked={isFullOrderCancellation}
          onChange={(e) => setIsFullOrderCancellation(e.target.checked)}
        />
      </Group>

      {order.payment_method === 'card' && (
        <Group mb="md">
          <Select
            label="Refund Method"
            value={refundMethod}
            onChange={setRefundMethod}
            data={[
              { value: 'account_credit', label: 'Account Credit (default)' },
              { value: 'card', label: 'Refund to Original Card' }
            ]}
          />
        </Group>
      )}

      <TextInput
        label="Reason (optional)"
        placeholder="Reason for the refund"
        value={reason}
        onChange={(e) => setReason(e.currentTarget.value)}
        mb="md"
        maxLength={1000}
        aria-label="Refund reason (optional)"
      />

      <Group mb="md" align="flex-end">
        <FileInput
          label="Proof images (optional, for records only)"
          placeholder="Upload images"
          accept="image/png,image/jpeg,image/webp,image/gif"
          multiple
          clearable
          onChange={handleImageUpload}
          w={320}
          aria-label="Upload refund proof images"
        />
        {images.length > 0 && (
          <Text size="xs" c="dimmed">{images.length} uploaded</Text>
        )}
      </Group>
      {images.length > 0 && (
        <Group mb="md" spacing="xs">
          {images.map((img) => (
            <div key={img.file_id} style={{ position: 'relative', display: 'inline-block' }}>
              <img
                src={img.previewUrl}
                alt={img.name || 'Refund image'}
                style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }}
              />
              <Button
                size="compact-xs"
                variant="filled"
                color="red"
                radius="xl"
                style={{ position: 'absolute', top: -6, right: -6 }}
                onClick={() => removeImage(img.file_id)}
                aria-label={`Remove ${img.name || 'image'}`}
              >
                ✕
              </Button>
            </div>
          ))}
        </Group>
      )}

      {/* Past Refunds */}
      {order.refunds && order.refunds.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h5>Past Refunds</h5>
          {order.refunds.map((r) => (
            <div key={r.id} mb="xs" p="xs" style={{ border: '1px solid #eee', borderRadius: 4, marginBottom: 8 }}>
              <Group justify="apart">
                <Text size="sm">
                  <b>Credit Note #{r.id.substring(0,8)}</b> - {new Date(r.created_at).toLocaleDateString()} - {formatPrice(r.total_refund_amount)}
                  {r.status === 'failed' ? <Badge color="red" ml="sm">Failed</Badge> : null}
                </Text>
                <Group>
                  <Button size="xs" variant="light" onClick={() => window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/orders/refunds/${r.id}/credit-note`, '_blank')}>
                    Download Credit Note
                  </Button>
                  {r.status === 'failed' && r.refund_method === 'card' && (
                    <Button size="xs" color="indigo.7" onClick={() => api.retryRefund(r.id).then(onRefundSuccess)}>
                      Retry
                    </Button>
                  )}
                </Group>
              </Group>
              {r.reason ? (
                <Text size="xs" c="dimmed" mt="xs">
                  <b>Reason:</b> {r.reason}
                </Text>
              ) : null}
              {r.images && r.images.length > 0 && (
                <Group mt="xs" spacing="xs">
                  {r.images.map((img) => (
                    <img
                      key={img.file_id}
                      src={`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/files/${img.file_id}`}
                      alt={img.filename || 'Refund image'}
                      style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 4, cursor: 'pointer', border: '1px solid #ddd' }}
                      onClick={() => window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/files/${img.file_id}`, '_blank')}
                    />
                  ))}
                </Group>
              )}
            </div>
          ))}
        </div>
      )}

      {/* New Refund Form */}
      <Table mb="md">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Product</Table.Th>
            <Table.Th>Unit Price</Table.Th>
            <Table.Th>Max Refundable</Table.Th>
            <Table.Th>Calc Type</Table.Th>
            <Table.Th>Rate/Amount</Table.Th>
            <Table.Th>Refund Amount</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {itemsData.map(item => (
            <Table.Tr key={item.order_item_id}>
              <Table.Td>{item.name}</Table.Td>
              <Table.Td>{formatPrice(item.unit_price)}</Table.Td>
              <Table.Td>{formatPrice(item.max_refundable)}</Table.Td>
              <Table.Td>
                <Select
                  value={item.calculation_type}
                  onChange={(v) => handleCalculationTypeChange(item.order_item_id, v)}
                  data={[
                    { value: 'fixed', label: 'Fixed' },
                    { value: 'percentage', label: 'Percentage' },
                    { value: 'quantity', label: 'Pcs' }
                  ]}
                  style={{ minWidth: 110 }}
                  disabled={isFullOrderCancellation}
                  aria-label={`Calculation type for ${item.name}`}
                />
              </Table.Td>
              <Table.Td>
                {item.calculation_type === 'percentage' ? (
                  <NumberInput
                    value={item.percentage_rate}
                    onChange={(v) => handlePercentageChange(item.order_item_id, v)}
                    min={0}
                    max={100}
                    hideControls
                    disabled={isFullOrderCancellation}
                    aria-label={`Percentage for ${item.name}`}
                    rightSection={<Text size="xs">%</Text>}
                  />
                ) : item.calculation_type === 'quantity' ? (
                  <NumberInput
                    value={item.refund_qty}
                    onChange={(v) => handleQuantityChange(item.order_item_id, v || 0)}
                    min={0}
                    max={item.ordered}
                    step={1}
                    hideControls
                    disabled={isFullOrderCancellation}
                    aria-label={`Pieces to refund for ${item.name}`}
                    rightSection={<Text size="xs">Stk</Text>}
                  />
                ) : (
                  <NumberInput
                    value={item.refund_amount}
                    onChange={(val) => handleRefundAmountChange(item.order_item_id, val || 0)}
                    min={0}
                    hideControls
                    clampValueOnBlur={false}
                    disabled={isFullOrderCancellation}
                    aria-label={`Fixed amount for ${item.name}`}
                  />
                )}
              </Table.Td>
              <Table.Td><b style={{ color: item.refund_amount > item.max_refundable ? 'red' : 'inherit' }}>{formatPrice(item.refund_amount || 0)}</b></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <Group justify="apart">
        <Text weight={700}>Total Refund: {formatPrice(totalRefundAmount)}</Text>
        <Group spacing="sm">
          <Button onClick={handlePreview} loading={previewing} variant="light" color="gray">
            Preview Credit Note
          </Button>
          <Button onClick={handleRefund} loading={submitting} color="indigo.7">
            Process Refund & Send Credit Note
          </Button>
        </Group>
      </Group>
    </Paper>
  );
}

export default function AdminOrders() {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const urlOrderId = searchParams.get('orderId');

  // Filters & State
  const [statusFilter, setStatusFilter] = useState(null);
  const [paymentFilter, setPaymentFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRows, setExpandedRows] = useState([]);
  const [refundOpenIds, setRefundOpenIds] = useState([]);
  // Which running monthly order the OngoingOrderModal is showing, if any.
  const [ongoingOrderId, setOngoingOrderId] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const data = await api.getAdminOrders();
      // Fetch refunds for each order
      const ordersWithRefunds = await Promise.all(
        data.map(async (order) => {
          try {
            const refunds = await api.getOrderRefunds(order.id);
            return { ...order, refunds };
          } catch {
            return { ...order, refunds: [] };
          }
        })
      );
      setOrders(ordersWithRefunds);
      if (urlOrderId && data.some(o => o.id === urlOrderId)) {
        setExpandedRows([urlOrderId]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrder = async (orderId, newStatus, newPaymentStatus) => {
    try {
      await api.updateAdminOrder(orderId, { status: newStatus, payment_status: newPaymentStatus });
      fetchOrders(); 
    } catch (err) {
      console.error('Failed to update order', err);
    }
  };

  const handleResendInvoice = async (orderId) => {
    const notifId = notifications.show({ loading: true, title: 'Sending', message: 'Sending invoice email...', autoClose: false });
    try {
      await api.resendInvoice(orderId);
      notifications.update({ id: notifId, color: 'teal', title: 'Success', message: 'Invoice resent to customer!', autoClose: 3000 });
    } catch (err) {
      notifications.update({ id: notifId, color: 'red', title: 'Error', message: err.message, autoClose: 5000 });
    }
  };

  const toggleRow = (id) => {
    setExpandedRows(prev =>
      prev.includes(id) ? [] : [id]
    );
  };

  const handleRowKeyDown = (e, id) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleRow(id);
    }
  };

  const filteredOrders = orders.filter(o => {
    if (statusFilter === 'running' && !isOpenRunningOrder(o)) return false;
    if (statusFilter === 'closed_running' && !isClosedRunningOrder(o)) return false;
    if (statusFilter && !['running', 'closed_running'].includes(statusFilter) && o.status !== statusFilter) return false;
    if (paymentFilter && o.payment_status !== paymentFilter) return false;

    if (searchQuery) {
      const sq = searchQuery.toLowerCase();
      const matchesSearch = 
        o.customer_name?.toLowerCase().includes(sq) ||
        o.customer_email?.toLowerCase().includes(sq) ||
        o.id.toLowerCase().includes(sq);
      if (!matchesSearch) return false;
    }

    return true;
  });

  const groupedOrders = useMemo(() => {
    const groups = {};
    filteredOrders.forEach(o => {
      const d = new Date(o.created_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(o);
    });
    return groups;
  }, [filteredOrders]);

  const sortedGroupKeys = useMemo(() =>
    Object.keys(groupedOrders).sort().reverse(),
    [groupedOrders]
  );

  const monthLocale = t.langCode === 'de' ? 'de-CH' : 'en-US';

  if (loading) return <Loader role="status" aria-live="polite" />;

  return (
    <div className="dashboard-view-wrapper">
      <header className="dashboard-page-header">
        <div>
          <h1 className="dashboard-page-title">Order Management</h1>
          <p className="dashboard-page-description">View, manage, and fulfill customer orders globally.</p>
        </div>
      </header>

      <Paper withBorder p="sm" mb="md" role="group" aria-label="Order filters">
        <Group gap="sm">
          <TextInput
            placeholder="Search orders by ID, customer name, or email…"
            leftSection={<IconSearch size={16} aria-hidden="true" />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            style={{ flex: 1, minWidth: 240 }}
            aria-label="Search orders by ID, customer name, or email"
          />
          <Select
            placeholder="All order statuses"
            data={[
              { value: 'pending', label: 'Pending' },
              { value: 'confirmed', label: 'Confirmed' },
              { value: 'partially_shipped', label: 'Partially Shipped' },
              { value: 'shipped', label: 'Shipped' },
              { value: 'cancelled', label: 'Cancelled' },
              { value: 'refunded', label: 'Refunded' },
              // Running/closed is an aggregation state, not a status — see
              // utils/runningOrder.js. Listed as its own pseudo-filter entries.
              { value: 'running', label: 'Running' },
              { value: 'closed_running', label: 'Running (closed)' },
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
            clearable
            w={200}
            aria-label="Filter by order status"
          />
          <Select
            placeholder="All payment statuses"
            data={[
              { value: 'paid', label: 'Paid' },
              { value: 'unpaid', label: 'Unpaid (or Monthly Invoice)' },
              { value: 'overdue', label: 'Overdue' },
            ]}
            value={paymentFilter}
            onChange={setPaymentFilter}
            clearable
            w={220}
            aria-label="Filter by payment status"
          />
        </Group>
      </Paper>

      <Paper withBorder>
        <Table.ScrollContainer minWidth={1100}>
          <Table highlightOnHover verticalSpacing="md" horizontalSpacing="md">
            <caption className="sr-only">Orders list with customer, total, status, payment, and actions</caption>
            <Table.Thead>
              <Table.Tr>
                <Table.Th scope="col">Order ID</Table.Th>
                <Table.Th scope="col">Customer</Table.Th>
                <Table.Th scope="col">Date</Table.Th>
                <Table.Th scope="col">Total (CHF)</Table.Th>
                <Table.Th scope="col">Status</Table.Th>
                <Table.Th scope="col">Payment Status</Table.Th>
                <Table.Th scope="col">Email</Table.Th>
                <Table.Th scope="col">Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortedGroupKeys.map(groupKey => {
                const [year, month] = groupKey.split('-');
                const monthName = new Date(year, month - 1).toLocaleDateString(monthLocale, { year: 'numeric', month: 'long' });
                return (
                  <React.Fragment key={groupKey}>
                    <Table.Tr>
                      <Table.Th scope="colgroup" colSpan={8} style={{ background: 'var(--mantine-color-gray-1)', borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
                        <Text fw={700} size="sm">{monthName}</Text>
                      </Table.Th>
                    </Table.Tr>
                    {groupedOrders[groupKey].map(o => {
                const isExpanded = expandedRows.includes(o.id);
                const fulfillmentsCount = (o.fulfillments || []).length;
                const creditNotesCount = (o.refunds || []).filter(r => r.credit_note_file_id).length;

                return (
                  <React.Fragment key={o.id}>
                    {/* Main Order Row */}
                    <Table.Tr
                      onClick={() => toggleRow(o.id)}
                      onKeyDown={(e) => handleRowKeyDown(e, o.id)}
                      tabIndex={0}
                      aria-expanded={isExpanded}
                      aria-controls={`admin-order-detail-${o.id}`}
                      aria-label={`Toggle details for order ${o.id.substring(0, 8)}`}
                      style={{ cursor: 'pointer' }}
                    >
                      <Table.Th scope="row">
                        <Group gap={4}>
                          <span>{o.id.substring(0, 8)}…</span>
                          {isExpanded ? <IconChevronDown size={14} aria-hidden="true" /> : <IconChevronRight size={14} aria-hidden="true" />}
                        </Group>
                      </Table.Th>
                      <Table.Td>
                        <Text fw={500}>{o.customer_name}</Text>
                        <Text size="xs" c="dimmed">{o.customer_email}</Text>
                      </Table.Td>
                      <Table.Td>{new Date(o.created_at).toLocaleDateString()}</Table.Td>
                      <Table.Td>{formatPrice(o.total_price)}</Table.Td>
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        {/* nowrap keeps this cell a single line whatever the row
                            contains — stacking the badge under the block-level Select
                            made running rows taller and broke the column's alignment. */}
                        <Group gap={6} wrap="nowrap" align="center">
                          <Select
                            size="xs"
                            value={o.status}
                            onChange={(v) => handleUpdateOrder(o.id, v, o.payment_status)}
                            data={[
                              { value: 'pending', label: 'Pending' },
                              { value: 'confirmed', label: 'Confirmed' },
                              { value: 'partially_shipped', label: 'Partially Shipped' },
                              { value: 'shipped', label: 'Shipped' },
                              { value: 'cancelled', label: 'Cancelled' },
                              { value: 'refunded', label: 'Refunded' },
                            ]}
                            allowDeselect={false}
                            w={130}
                            style={{ flexShrink: 0 }}
                            aria-label={`Order status for order ${o.id.substring(0, 8)}`}
                          />
                          {/* Aggregation is not a status, so it is shown as a badge
                              beside the lifecycle Select rather than as one of its
                              options. The Select stays lifecycle-only, which is what
                              the server accepts on PUT /orders/:id. */}
                          {isRunningOrder(o) && (
                            <Badge
                              size="xs"
                              variant="light"
                              style={{ flexShrink: 0 }}
                              color={isOpenRunningOrder(o) ? 'blue' : 'green'}
                              title={isOpenRunningOrder(o)
                                ? 'Running monthly order — still accepting batches. Open it with the Running button.'
                                : 'This running monthly order has been closed and will be billed on its monthly invoice.'}
                            >
                              {isOpenRunningOrder(o) ? 'Running' : 'Closed'}
                            </Badge>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        <Select
                          size="xs"
                          value={o.payment_status}
                          onChange={(v) => handleUpdateOrder(o.id, o.status, v)}
                          data={[
                            { value: 'unpaid', label: 'Unpaid' },
                            { value: 'paid', label: 'Paid' },
                            { value: 'overdue', label: 'Overdue' },
                          ]}
                          allowDeselect={false}
                          w={120}
                          aria-label={`Payment status for order ${o.id.substring(0, 8)}`}
                        />
                      </Table.Td>
                      <Table.Td>
                        {o.email_status ? (
                          <Badge
                            color={o.email_status === 'sent' ? 'green' : o.email_status === 'failed' ? 'red' : 'yellow'}
                            size="sm"
                            variant="light"
                          >
                            {o.email_status === 'sent' ? '✓ Sent' : o.email_status === 'failed' ? '✗ Failed' : '⏳ Pending'}
                          </Badge>
                        ) : (
                          <Text size="sm" c="dimmed">—</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <Button variant="subtle" size="xs" leftSection={<IconDownload size={14} aria-hidden="true" />}
                            aria-label={`Download invoice for order ${o.id.substring(0, 8)}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              window.open(`${process.env.REACT_APP_API_URL}/orders/${o.id}/invoice`, '_blank');
                            }}
                          >
                            Invoice
                          </Button>

                          {o.payment_method !== 'monthly' && (
                            <Button variant="subtle" size="xs" color="green" leftSection={<IconMail size={14} />}
                              aria-label={`Resend invoice for order ${o.id.substring(0, 8)}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleResendInvoice(o.id);
                              }}
                            >
                              Resend
                            </Button>
                          )}

                          {/* A running monthly order is managed from its own modal.
                              Shown for closed ones too, so the finalised state is still
                              reviewable — the modal disables the mutating controls. */}
                          {isRunningOrder(o) && (
                            <Button
                              variant="light"
                              size="xs"
                              color="blue"
                              leftSection={<IconRepeat size={14} aria-hidden="true" />}
                              aria-label={`Open running order ${o.id.substring(0, 8)}${isOpenRunningOrder(o) ? '' : ' (closed)'}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setOngoingOrderId(o.id);
                              }}
                            >
                              Running
                            </Button>
                          )}

                          {fulfillmentsCount > 0 && (
                            <Badge size="sm" variant="light" color="blue" aria-label={`${fulfillmentsCount} delivery notes`}>
                              {fulfillmentsCount} Delivery Notes
                            </Badge>
                          )}
                          {creditNotesCount > 0 && (
                            <Badge size="sm" variant="light" color="orange" aria-label={`${creditNotesCount} credit notes`}>
                              {creditNotesCount} Credit Notes
                            </Badge>
                          )}
                        </Group>
                      </Table.Td>
                    </Table.Tr>

                    {/* Nested Items Row */}
                    {isExpanded && (
                      <Table.Tr id={`admin-order-detail-${o.id}`} style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                        <Table.Td colSpan={8} p="md">
                          <div style={{ marginBottom: '1rem' }}>
                            <h4>Order Items & Fulfillment</h4>
                            <FulfillOrderForm order={o} onFulfillSuccess={fetchOrders} />
                          </div>
                          <div>
                            <Button
                              variant="light"
                              color="red"
                              size="xs"
                              onClick={() => setRefundOpenIds(prev =>
                                prev.includes(o.id) ? prev.filter(id => id !== o.id) : [...prev, o.id]
                              )}
                              leftSection={refundOpenIds.includes(o.id) ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                              mb="sm"
                            >
                              {refundOpenIds.includes(o.id) ? 'Hide Refund' : 'Process Refund'}
                            </Button>
                            {refundOpenIds.includes(o.id) && (
                              <div>
                                {o.payment_status === 'paid' ? (
                                  <RefundOrderForm order={o} onRefundSuccess={fetchOrders} />
                                ) : (
                                  <p style={{ color: '#666', fontStyle: 'italic' }}>
                                    {isRunningOrder(o)
                                      ? 'Nothing has been collected yet — this running order is settled by its monthly invoice. Reduce it with a money-back adjustment instead.'
                                      : `Refunds are only available for orders with paid status. Current payment status: ${o.payment_status}`}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </React.Fragment>
                );
              })}
                  </React.Fragment>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>

      {ongoingOrderId && (
        <OngoingOrderModal
          orderId={ongoingOrderId}
          onChanged={fetchOrders}
          onClose={() => setOngoingOrderId(null)}
        />
      )}
    </div>
  );
}